import {registerGlobal} from "./core.js";

class DateTimeDropdown {
    constructor(containerId, options = {}) {
        this.container = document.getElementById(containerId);
        this.options = Object.assign({
            mode: "date",          // "date" | "datetime" | "range"
            format: "Y-m-d H:i",   // PHP-style format supported by Datetime.format()
            placeholder: "Select date...",
            withTime: false,
            range: false,
            autoFlip: true,
            name: containerId || "datetime",
            onChange: null,
        }, options);

        this.isOpen = false;
        this.selectedStart = null;
        this.selectedEnd = null;
        this.selectedTime = {hour: 0, minute: 0};
        this.currentMonth = new Date();

        this._render();
        this._buildCalendar();
        this._attachEvents();
        this._syncValue();

        Object.defineProperty(this.container, "value", {
            configurable: true,
            get: () => this.getValue(),
            set: (val) => this.setValue(val),
        });
    }

    // ---------- BUILD ----------
    _render() {
        this.container.innerHTML = "";

        // Display box
        this.display = document.createElement("div");
        this.display.className = "datetime-display";
        this.display.innerText = this.options.placeholder;
        this.container.appendChild(this.display);

        // Hidden input
        this.hiddenInput = document.createElement("input");
        this.hiddenInput.type = "hidden";
        this.hiddenInput.name = this.options.name;
        this.container.appendChild(this.hiddenInput);

        // Dropdown wrapper
        this.dropdownMenu = document.createElement("div");
        this.dropdownMenu.className = "datetime-menu";
        this.container.appendChild(this.dropdownMenu);

        // Navigation
        const nav = document.createElement("div");
        nav.className = "datetime-nav";
        nav.innerHTML = `
    <button type="button" class="prev-month">‹</button>
    <span class="month-label"></span>
    <button type="button" class="next-month">›</button>
  `;
        this.dropdownMenu.appendChild(nav);
        const monthLabel = nav.querySelector(".month-label");
        monthLabel.addEventListener("click", () => this._toggleMonthYearView());
        monthLabel.innerText = `${this.currentMonth.toLocaleString("default", {month: "long"})} ${this.currentMonth.getFullYear()}`;

        // Calendar
        this.calendar = document.createElement("div");
        this.calendar.className = "datetime-calendar";
        this.dropdownMenu.appendChild(this.calendar);

        // 🔹 single time selector + Apply button if datetime
        if (this.options.mode === "datetime" || this.options.withTime) {
            const timeBox = document.createElement("div");
            timeBox.className = "datetime-time";
            timeBox.innerHTML = `
      <label>Time:</label>
      <select class="hour"></select> :
      <select class="minute"></select>
    `;
            this.dropdownMenu.appendChild(timeBox);
            this._buildTimeSelectors(timeBox);

            const actions = document.createElement("div");
            actions.className = "datetime-actions";
            actions.innerHTML = `<button type="button" class="apply-btn">Apply</button>`;
            this.dropdownMenu.appendChild(actions);
        }

        // Divider for range mode
        if (this.options.mode === "range" || this.options.range) {
            const divider = document.createElement("div");
            divider.className = "datetime-divider";
            this.container.appendChild(divider);
            this.container.classList.add("range-mode");
        }

        this._addPresets();
    }

    _buildTimeSelectors(timeBox) {
        const hourSel = timeBox.querySelector(".hour");
        const minuteSel = timeBox.querySelector(".minute");

        for (let h = 0; h < 24; h++) {
            const opt = document.createElement("option");
            opt.value = h;
            opt.text = h.toString().padStart(2, "0");
            hourSel.appendChild(opt);
        }
        for (let m = 0; m < 60; m += 1) {
            const opt = document.createElement("option");
            opt.value = m;
            opt.text = m.toString().padStart(2, "0");
            minuteSel.appendChild(opt);
        }

        hourSel.addEventListener("change", () => {
            this.selectedTime.hour = parseInt(hourSel.value);
            if (this.selectedStart) {
                this.selectedStart.setHours(this.selectedTime.hour);
            }
            this._syncValue();
        });

        minuteSel.addEventListener("change", () => {
            this.selectedTime.minute = parseInt(minuteSel.value);
            if (this.selectedStart) {
                this.selectedStart.setMinutes(this.selectedTime.minute);
            }
            this._syncValue();
        });
    }

    _updateTimeSelectors() {
        const hourSel = this.dropdownMenu.querySelector(".hour");
        const minuteSel = this.dropdownMenu.querySelector(".minute");

        if (!hourSel || !minuteSel) return;

        hourSel.value = this.selectedTime.hour;
        minuteSel.value = this.selectedTime.minute;
    }

    // ---------- CALENDAR ----------

    _buildCalendar() {
        // 🟦 Month picker view
        const y = this.currentMonth.getFullYear();
        const m = this.currentMonth.getMonth();

        // 🟦 Handle Month picker
        if (this.viewMode === "month") {
            const currentMonth = new Date().getMonth();
            const monthLabel = this.dropdownMenu.querySelector(".month-label");
            monthLabel.textContent = y;
            let html = `<div class="month-picker">`;
            const monthNames = Array.from({length: 12}, (_, i) =>
                new Date(0, i).toLocaleString("default", {month: "short"})
            );
            monthNames.forEach((name, idx) => {
                const isCurrent = idx === currentMonth ? " current" : "";
                html += `<div class="month-item${isCurrent}" data-month="${idx}">${name}</div>`;
            });
            html += `</div>`;
            this.calendar.innerHTML = html;

            this.calendar.querySelectorAll(".month-item").forEach((el) => {
                el.addEventListener("click", (ev) => this._selectMonth(+el.dataset.month, ev));
            });
            return;
        }

        // 🟩 Handle Year picker
        if (this.viewMode === "year") {
            const currentYear = y;
            const startYear = currentYear - 6;
            const endYear = currentYear + 5;
            const monthLabel = this.dropdownMenu.querySelector(".month-label");
            monthLabel.textContent = `${startYear} – ${endYear}`;
            let html = `<div class="year-picker">`;
            for (let yy = startYear; yy <= endYear; yy++) {
                const isCurrent = yy === currentYear ? " current" : "";
                html += `<div class="year-item${isCurrent}" data-year="${yy}">${yy}</div>`;
            }
            html += `</div>`;
            this.calendar.innerHTML = html;

            this.calendar.querySelectorAll(".year-item").forEach((el) => {
                el.addEventListener("click", (ev) => this._selectYear(+el.dataset.year, ev));
            });
            return;
        }

        this.viewMode = "calendar";

        const isRange = this.options.range || this.options.mode === "range";

        const buildSingleMonth = (year, month) => {
            const firstDay = new Date(year, month, 1);
            const startDay = firstDay.getDay();
            const daysInMonth = new Date(year, month + 1, 0).getDate();
            const label = `${firstDay.toLocaleString("default", {month: "long"})} ${year}`;
            const monthLabel = this.dropdownMenu.querySelector(".month-label");
            if (monthLabel) {
                if (isRange) {
                    const nextMonth = (m + 1) % 12;
                    const nextYear = m === 11 ? y + 1 : y;

                    const firstMonthName = new Date(y, m, 1).toLocaleString("default", {month: "long"});
                    const secondMonthName = new Date(nextYear, nextMonth, 1).toLocaleString("default", {month: "long"});

                    // 🧮 Build combined header, smart cross-year support
                    monthLabel.textContent = y === nextYear
                        ? `${firstMonthName} – ${secondMonthName} ${y}`
                        : `${firstMonthName} ${y} – ${secondMonthName} ${nextYear}`;
                } else {
                    // single mode
                    monthLabel.textContent = `${new Date(y, m, 1).toLocaleString("default", {month: "long"})} ${y}`;
                }
            }
            let html = `<div class="datetime-calendar">
    <div class="calendar-grid">`;

            const dayNames = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
            html += dayNames.map(d => `<div class="day-name">${d}</div>`).join("");

            for (let i = 0; i < startDay; i++) html += `<div class="day empty"></div>`;
            for (let d = 1; d <= daysInMonth; d++) {
                const dateStr = `${year}-${(month + 1).toString().padStart(2, "0")}-${d.toString().padStart(2, "0")}`;
                html += `<div class="day" data-date="${dateStr}">${d}</div>`;
            }

            html += `</div></div>`;
            return html;
        };

        if (isRange) {
            const nextMonth = (m + 1) % 12;
            const nextYear = m === 11 ? y + 1 : y;
            this.calendar.innerHTML = `
        <div class="calendar-dual">
            ${buildSingleMonth(y, m)}
            ${buildSingleMonth(nextYear, nextMonth)}
        </div>
    `;
        } else {
            this.calendar.innerHTML = buildSingleMonth(y, m);
        }


        this._highlightSelected();
    }

    _toggleMonthYearView() {
        if (this.viewMode === "calendar") {
            this.viewMode = "month";
        } else if (this.viewMode === "month") {
            this.viewMode = "year";
        } else {
            this.viewMode = "calendar";
        }

        this._buildCalendar();
        this.dropdownMenu.classList.add("show");
        this.isOpen = true;
    }

    _selectMonth(m, event) {
        if (event) event.stopPropagation(); // 🧱 Stop bubbling!
        this.currentMonth.setMonth(m);
        this.viewMode = "calendar";
        this._buildCalendar();
        this._highlightSelected();

        this.isOpen = true;
        this.dropdownMenu.classList.add("show");
    }

    _selectYear(y, event) {
        if (event) event.stopPropagation(); // 🧱 Stop bubbling!
        this.currentMonth.setFullYear(y);
        this.viewMode = "month";
        this._buildCalendar();
        this._highlightSelected();

        this.isOpen = true;
        this.dropdownMenu.classList.add("show");
    }

    _addPresets() {
        if (this.presetsBox) this.presetsBox.remove();

        const presets = document.createElement("div");
        presets.className = "datetime-presets";

        // Build preset buttons based on mode
        if (this.options.mode === "date" && !this.options.range) {
            presets.innerHTML = `<button type="button" data-preset="today">Today</button>`;
        } else if (this.options.mode === "datetime" || this.options.withTime) {
            presets.innerHTML = `<button type="button" data-preset="now">Now</button>`;
        } else if (this.options.mode === "range" || this.options.range) {
            presets.innerHTML = `
      <button type="button" data-preset="today">Today</button>
      <button type="button" data-preset="yesterday">Yesterday</button>
      <button type="button" data-preset="7days">Last 7 Days</button>
      <button type="button" data-preset="month">This Month</button>
      <button type="button" data-preset="lastmonth">Last Month</button>
    `;
        }

        this.dropdownMenu.appendChild(presets);
        this.presetsBox = presets;

        void presets.offsetWidth;
        presets.style.opacity = "1";

        presets.querySelectorAll("button").forEach(btn => {
            btn.addEventListener("click", () => this._applyPreset(btn.dataset.preset));
        });
    }

    _applyPreset(type) {
        const now = new Date();

        const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
        const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);

        const startOfLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const endOfLastMonth = new Date(now.getFullYear(), now.getMonth(), 0);

        switch (type) {
            case "today":
                if (this.options.range || this.options.mode === "range") {
                    this.selectedStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
                    this.selectedEnd = new Date(now.getFullYear(), now.getMonth(), now.getDate());
                } else {
                    this.selectedStart = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0);
                }
                break;

            case "yesterday":
                const y = new Date(now);
                y.setDate(now.getDate() - 1);
                this.selectedStart = new Date(y.getFullYear(), y.getMonth(), y.getDate());
                this.selectedEnd = new Date(y.getFullYear(), y.getMonth(), y.getDate());
                break;

            case "7days":
                const seven = new Date(now);
                seven.setDate(now.getDate() - 6);
                this.selectedStart = new Date(seven.getFullYear(), seven.getMonth(), seven.getDate());
                this.selectedEnd = new Date(now.getFullYear(), now.getMonth(), now.getDate());
                break;

            case "month":
                this.selectedStart = startOfMonth;
                this.selectedEnd = endOfMonth;
                break;

            case "lastmonth":
                this.selectedStart = startOfLastMonth;
                this.selectedEnd = endOfLastMonth;
                break;

            case "now":
                this.selectedStart = new Date();
                this.selectedTime.hour = this.selectedStart.getHours();
                this.selectedTime.minute = this.selectedStart.getMinutes();
                break;
        }

        this._buildCalendar();
        this._highlightSelected();
        this._updateTimeSelectors();
        this._syncValue();
        this.close();
    }

    _attachEvents() {
        this.display.addEventListener("click", () => this.toggle());
        this.dropdownMenu.querySelector(".prev-month").addEventListener("click", () => {
            this.currentMonth.setMonth(this.currentMonth.getMonth() - 1);
            this._buildCalendar();
        });
        this.dropdownMenu.querySelector(".next-month").addEventListener("click", () => {
            this.currentMonth.setMonth(this.currentMonth.getMonth() + 1);
            this._buildCalendar();
        });
        this.dropdownMenu.addEventListener("click", (e) => {
            if (e.target.classList.contains("day") && !e.target.classList.contains("empty")) {
                this._selectDate(e.target.dataset.date);
            }
        });
        document.addEventListener("click", (e) => {
            if (this.dropdownMenu.contains(e.target) || this.container.contains(e.target)) {
                return; // 🧱 Don't close if click inside dropdown
            }
            this.close();
        });
        const applyBtn = this.dropdownMenu.querySelector(".apply-btn");
        if (applyBtn) {
            applyBtn.addEventListener("click", (e) => {
                e.preventDefault();
                // ensure we have a base date
                if (!this.selectedStart) {
                    const now = new Date();
                    this.selectedStart = new Date(
                        now.getFullYear(),
                        now.getMonth(),
                        now.getDate(),
                        this.selectedTime.hour,
                        this.selectedTime.minute
                    );
                } else {
                    this.selectedStart.setHours(this.selectedTime.hour);
                    this.selectedStart.setMinutes(this.selectedTime.minute);
                }
                this._syncValue();
                this.close();
            });
        }
    }

    // ---------- SELECTION ----------
    _selectDate(dateStr) {
        const [y, m, d] = dateStr.split("-").map(Number);
        // Create local date (no timezone shift)
        const date = new Date(y, m - 1, d, this.selectedTime.hour, this.selectedTime.minute);

        if (this.options.range || this.options.mode === "range") {
            if (!this.selectedStart || (this.selectedStart && this.selectedEnd)) {
                this.selectedStart = date;
                this.selectedEnd = null;
            } else {
                if (date < this.selectedStart) {
                    this.selectedEnd = this.selectedStart;
                    this.selectedStart = date;
                } else {
                    this.selectedEnd = date;
                }
                this.close();
            }
        } else {
            this.selectedStart = date;

            // 🟩 If datetime, wait for Apply instead of closing
            if (this.options.mode === "datetime" || this.options.withTime) {
                this._syncValue(); // show date but stay open
            } else {
                this.close();
            }
        }

        this._highlightSelected();
        this._syncValue();
    }

    _highlightSelected() {
        this.calendar.querySelectorAll(".day").forEach((day) => {
            day.classList.remove("selected", "in-range");
            const d = day.dataset.date;
            if (!d) return;
            const [y, m, dd] = d.split("-").map(Number);
            const dateObj = new Date(y, m - 1, dd);

            if (this.selectedStart && dateObj.toDateString() === this.selectedStart.toDateString()) {
                day.classList.add("selected");
            }
            if (this.selectedEnd && dateObj.toDateString() === this.selectedEnd.toDateString()) {
                day.classList.add("selected");
            }
            if (
                this.selectedStart &&
                this.selectedEnd &&
                dateObj > this.selectedStart &&
                dateObj < this.selectedEnd
            ) {
                day.classList.add("in-range");
            }
        });
    }

    // ---------- VALUE ----------
    _syncValue() {
        let value = "";

        if (this.selectedStart) {
            if (this.options.range || this.options.mode === "range") {
                const start = Datetime.format(this.selectedStart, this.options.format);
                const end = this.selectedEnd ? Datetime.format(this.selectedEnd, this.options.format) : "";
                value = `${start}${end ? " - " + end : ""}`;
            } else {
                value = Datetime.format(this.selectedStart, this.options.format);
            }
        }

        this.display.innerText = value || this.options.placeholder;
        this.hiddenInput.value = value;
        this.container.dataset.datetimeValue = value;
        this.container.setAttribute("value", value);

        if (this.options.onChange) this.options.onChange(value);
    }

    _parseISO(str) {
        const isoRegex = /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?$/;
        const m = str.match(isoRegex);
        if (!m) return null;
        const [_, y, mo, d, h = "0", mi = "0", s = "0"] = m;
        return new Date(Number(y), Number(mo) - 1, Number(d), Number(h), Number(mi), Number(s));
    }

    // ---------- TOGGLE ----------
    toggle() {
        this.isOpen ? this.close() : this.open();
    }

    open() {
        if (!this.dropdownMenu) return;

        // 🕐 Sync time selectors with current selected date before opening
        if (this.selectedStart) {
            this.selectedTime.hour = this.selectedStart.getHours();
            this.selectedTime.minute = this.selectedStart.getMinutes();
            this._updateTimeSelectors();
        }

        // AutoFlip logic continues...
        this.dropdownMenu.style.visibility = "hidden";
        this.dropdownMenu.style.display = "block";
        const rect = this.container.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const dropdownHeight = this.dropdownMenu.scrollHeight || 200;
        this.dropdownMenu.style.visibility = "";
        this.dropdownMenu.style.display = "";

        if (this.selectedStart) {
            this.currentMonth = new Date(this.selectedStart.getFullYear(), this.selectedStart.getMonth(), 1);
            this._buildCalendar();
            this._highlightSelected();
            this._updateTimeSelectors();
        }

        const spaceBelow = viewportHeight - rect.bottom;
        const spaceAbove = rect.top;
        const flipUp = (this.options.autoFlip && spaceBelow < dropdownHeight && spaceAbove > spaceBelow);

        if (flipUp) {
            this.dropdownMenu.classList.add("flip-up");
            this.dropdownMenu.style.top = "auto";
            this.dropdownMenu.style.bottom = `${rect.height}px`;
        } else {
            this.dropdownMenu.classList.remove("flip-up");
            this.dropdownMenu.style.top = "100%";
            this.dropdownMenu.style.bottom = "auto";
        }

        this.dropdownMenu.classList.add("show");
        this.isOpen = true;
    }

    close() {
        this.dropdownMenu.classList.remove("show", "flip-up");
        this.dropdownMenu.style.top = "100%";
        this.dropdownMenu.style.bottom = "auto";
        this.isOpen = false;
    }

    // ---------- API ----------
    getValue() {
        return this.hiddenInput.value;
    }


    setValue(val, val2 = null) {
        // reset
        this.selectedStart = null;
        this.selectedEnd = null;

        const parseISO = (str) => {
            const m = str?.trim().match(
                /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?$/
            );
            if (!m) return null;
            const [_, y, mo, d, h = "0", mi = "0", s = "0"] = m;
            return new Date(+y, +mo - 1, +d, +h, +mi, +s);
        };

        if (this.options.range || this.options.mode === "range") {
            let start = null, end = null;

            if (val2) {
                start = parseISO(val);
                end = parseISO(val2);
            } else if (typeof val === "string") {
                const parts = val.split(",");
                if (parts.length >= 2) {
                    start = parseISO(parts[0]);
                    end = parseISO(parts[1]);
                } else {
                    start = parseISO(val);
                }
            }

            if (start && !isNaN(start)) this.selectedStart = start;
            if (end && !isNaN(end)) this.selectedEnd = end;

            if (this.selectedStart) {
                this.currentMonth = new Date(this.selectedStart.getFullYear(), this.selectedStart.getMonth(), 1);
            }

            // rebuild and highlight
            this._buildCalendar();
            this._highlightSelected();

            // let _syncValue() format and display properly
            this._syncValue();
        } else {
            const parsed = parseISO(val);
            if (!parsed) return;

            this.selectedStart = parsed;
            this.currentMonth = new Date(parsed.getFullYear(), parsed.getMonth(), 1);
            this.selectedTime = {
                hour: parsed.getHours(),
                minute: parsed.getMinutes(),
            };

            this._buildCalendar();
            this._highlightSelected();
            this._updateTimeSelectors();
            this._syncValue();
        }
    }
}

registerGlobal("DateTimeDropdown", DateTimeDropdown);